import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { euro, apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Btn, Modal, Field, inputCls, Badge, EmptyState, Toolbar } from "@/components/kit";
import { Plus, Trash2, Users, Pencil, Mail, Phone } from "lucide-react";

const EMPTY = { name: "", company: "", email: "", phone: "", type: "prospect", stage: "nouveau", value: 0, notes: "" };
const STAGES = ["nouveau", "contacté", "qualifié", "proposition", "gagné", "perdu"];

export default function CRM() {
  const { user } = useAuth();
  const [filter, setFilter] = useState("");
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => api.get(`/clients${filter ? `?type=${filter}` : ""}`).then((r) => setList(r.data)).catch(() => {});
  useEffect(() => { load(); }, [filter]);

  const openNew = () => { setForm(EMPTY); setEditId(null); setOpen(true); };
  const openEdit = (c) => { setForm({ ...EMPTY, ...c }); setEditId(c.id); setOpen(true); };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = { ...form, value: Number(form.value) };
    try {
      if (editId) await api.put(`/clients/${editId}`, payload);
      else await api.post("/clients", payload);
      toast.success(editId ? "Contact mis à jour" : "Contact ajouté");
      setOpen(false); load();
    } catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Supprimer ce contact ?")) return;
    try { await api.delete(`/clients/${id}`); toast.success("Supprimé"); load(); }
    catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
  };

  return (
    <div className="animate-fade-up" data-testid="crm-page">
      <Toolbar>
        <div className="flex border border-border">
          {[{ k: "", l: "Tous" }, { k: "prospect", l: "Prospects" }, { k: "client", l: "Clients" }].map((t) => (
            <button key={t.k} onClick={() => setFilter(t.k)} data-testid={`crm-filter-${t.k || "all"}`}
              className={`px-5 py-2 text-sm font-medium transition-colors ${filter === t.k ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-accent"}`}>
              {t.l}
            </button>
          ))}
        </div>
        <Btn onClick={openNew} data-testid="new-contact-btn"><Plus size={16} /> Nouveau contact</Btn>
      </Toolbar>

      {list.length === 0 ? (
        <EmptyState icon={Users} title="Aucun contact" sub="Ajoutez vos clients et prospects, suivez leur avancement dans le pipeline commercial." action={<Btn onClick={openNew}><Plus size={16} /> Ajouter</Btn>} />
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
          {list.map((c) => (
            <div key={c.id} className="border border-border bg-card p-5 transition-colors hover:border-primary/40" data-testid={`contact-card-${c.id}`}>
              <div className="mb-3 flex items-start justify-between">
                <div className="min-w-0">
                  <p className="truncate font-display text-base font-bold">{c.company || c.name}</p>
                  {c.company && <p className="truncate text-sm text-muted-foreground">{c.name}</p>}
                </div>
                <Badge>{c.type}</Badge>
              </div>
              <div className="space-y-1.5 text-sm text-muted-foreground">
                {c.email && <p className="flex items-center gap-2 truncate"><Mail size={14} /> {c.email}</p>}
                {c.phone && <p className="flex items-center gap-2"><Phone size={14} /> {c.phone}</p>}
              </div>
              <div className="mt-4 flex items-center justify-between border-t border-border pt-3">
                <div className="flex items-center gap-2">
                  <Badge>{c.stage}</Badge>
                  {c.value > 0 && <span className="font-mono text-sm text-primary">{euro(c.value)}</span>}
                </div>
                <div className="flex gap-1">
                  <button onClick={() => openEdit(c)} data-testid={`edit-contact-${c.id}`} className="p-1.5 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"><Pencil size={15} /></button>
                  {user?.role === "admin" && (
                    <button onClick={() => del(c.id)} data-testid={`delete-contact-${c.id}`} className="p-1.5 text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive"><Trash2 size={15} /></button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Modal open={open} onClose={() => setOpen(false)} title={editId ? "Modifier le contact" : "Nouveau contact"}>
        <form onSubmit={save} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <Field label="Nom du contact"><input required className={inputCls} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="contact-name" /></Field>
            <Field label="Entreprise"><input className={inputCls} value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} data-testid="contact-company" /></Field>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Field label="E-mail"><input type="email" className={inputCls} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="contact-email" /></Field>
            <Field label="Téléphone"><input className={inputCls} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="contact-phone" /></Field>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <Field label="Type">
              <select className={inputCls} value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} data-testid="contact-type">
                <option value="prospect">Prospect</option>
                <option value="client">Client</option>
              </select>
            </Field>
            <Field label="Étape">
              <select className={inputCls} value={form.stage} onChange={(e) => setForm({ ...form, stage: e.target.value })} data-testid="contact-stage">
                {STAGES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </Field>
            <Field label="Valeur (€)"><input type="number" step="0.01" className={inputCls} value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} data-testid="contact-value" /></Field>
          </div>
          <Field label="Notes"><textarea rows={3} className={inputCls} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="contact-notes" /></Field>
          <div className="flex justify-end gap-2 border-t border-border pt-4">
            <Btn type="button" variant="secondary" onClick={() => setOpen(false)}>Annuler</Btn>
            <Btn type="submit" loading={saving} data-testid="save-contact-btn">Enregistrer</Btn>
          </div>
        </form>
      </Modal>
    </div>
  );
}
